import {
  Component,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { DateTime } from 'luxon';
import { Subscription } from 'rxjs';
import { PaginationDTO } from 'src/app/common/types/responses/responses-template';
import { FileDTO } from '../../../dtos/file.dto';
import {
  ChatMessage,
  Conversation,
  GlaiMessageDTO,
} from '../../../dtos/glai.dto';
import { GlaiService } from '../../../services/glai/glai.service';
import { ModalComponent } from '../../modals/modal/modal.component';

export interface SelectedConversationPayload {
  conversation: Conversation;
  messages: ChatMessage[];
  mode?: string;
}

interface ParsedFullMessage {
  message?: string;
  files?: FileDTO[];
  modes?: string[];
}

@Component({
  selector: 'app-glai-previous-chats-modal',
  templateUrl: './glai-previous-chats-modal.component.html',
  styleUrl: './glai-previous-chats-modal.component.scss',
  standalone: false,
})
export class GlaiPreviousChatsModalComponent implements OnDestroy {
  @ViewChild('previousChatsModal') previousChatsModal: ModalComponent;

  @Output() readonly conversationSelected =
    new EventEmitter<SelectedConversationPayload>();

  previousChats: Conversation[] = [];

  chatHistorySearchTerm = '';

  chatHistoryDateFormat = 'MM/dd/yy';

  chatHistoryIsLoading = false;

  chatHistoryMeta: PaginationDTO;

  chatHistoryFilters = {
    page: 1,
    per_page: 7,
  };

  chatHistorySearchTimeout: ReturnType<typeof setTimeout>;

  subscriptions = new Subscription();

  constructor(private glaiService: GlaiService) {}

  open() {
    this.previousChatsModal.open();
    this.loadConversationHistory();
  }

  close() {
    this.resetChatHistoryState();
    this.previousChatsModal.close();
  }

  resetChatHistoryState() {
    this.chatHistorySearchTerm = '';
    this.chatHistoryFilters.page = 1;
    if (this.chatHistorySearchTimeout) {
      clearTimeout(this.chatHistorySearchTimeout);
    }
  }

  openConversation(conversation: Conversation) {
    this.chatHistoryIsLoading = true;
    this.glaiService.disconnectStream();
    this.subscriptions.add(
      this.glaiService.getConversation(conversation.id).subscribe({
        next: (response) => {
          const conversationMessages = response.item.messages;
          const selectedMode =
            GlaiPreviousChatsModalComponent.getModeFromLastMessage(
              conversationMessages
            );
          this.chatHistoryIsLoading = false;
          this.conversationSelected.emit({
            conversation: response.item,
            messages: conversationMessages.map((message) =>
              GlaiPreviousChatsModalComponent.mapGlaiMessageToChatMessage(
                message
              )
            ),
            mode: selectedMode,
          });
          this.close();
        },
        error: () => {
          this.chatHistoryIsLoading = false;
        },
      })
    );
  }

  static mapGlaiMessageToChatMessage(message: GlaiMessageDTO): ChatMessage {
    const parsedFullMessage = GlaiPreviousChatsModalComponent.parseFullMessage(
      message.full_message
    );
    const content =
      message.text.trim() || parsedFullMessage?.message?.trim() || '';

    const mappedMessage: ChatMessage = {
      id: `${message.id}`,
      content,
      rawContent: content,
      isComplete: true,
      timestamp: GlaiPreviousChatsModalComponent.convertTimestampToIso(
        message.created_at
      ),
      role: message.role === 'assistant' ? 'assistant' : 'user',
      files: GlaiPreviousChatsModalComponent.extractFiles(parsedFullMessage),
    };

    if (mappedMessage.role === 'assistant') {
      GlaiService.extractThinking(mappedMessage);
    }

    return mappedMessage;
  }

  static convertTimestampToIso(timestamp: number): string {
    const milliseconds = timestamp > 9999999999 ? timestamp : timestamp * 1000;
    return new Date(milliseconds).toISOString();
  }

  static parseFullMessage(fullMessage?: string): ParsedFullMessage | null {
    if (!fullMessage) {
      return null;
    }

    try {
      return JSON.parse(fullMessage) as ParsedFullMessage;
    } catch {
      return null;
    }
  }

  static extractFiles(parsedFullMessage: ParsedFullMessage | null): FileDTO[] {
    if (!parsedFullMessage || !Array.isArray(parsedFullMessage.files)) {
      return [];
    }

    return parsedFullMessage.files;
  }

  static getModeFromLastMessage(
    messages: GlaiMessageDTO[]
  ): string | undefined {
    if (!messages.length) {
      return undefined;
    }

    const lastMessage = messages[messages.length - 1];
    const parsedFullMessage = GlaiPreviousChatsModalComponent.parseFullMessage(
      lastMessage.full_message
    );

    if (!parsedFullMessage || !Array.isArray(parsedFullMessage.modes)) {
      return undefined;
    }

    const [firstMode] = parsedFullMessage.modes;
    if (typeof firstMode !== 'string' || !firstMode.trim()) {
      return undefined;
    }

    return firstMode;
  }

  getChatHistoryDisplayDate(timestamp: number): string {
    const seconds =
      timestamp > 9999999999 ? Math.floor(timestamp / 1000) : timestamp;
    const rowDate = DateTime.fromSeconds(seconds).startOf('day');
    const today = DateTime.local().startOf('day');
    const yesterday = today.minus({ days: 1 });

    if (rowDate.equals(today)) {
      return 'Today';
    }

    if (rowDate.equals(yesterday)) {
      return 'Yesterday';
    }

    return rowDate.toFormat(this.chatHistoryDateFormat);
  }

  loadConversationHistory() {
    this.chatHistoryIsLoading = true;
    this.subscriptions.add(
      this.glaiService
        .getConversationHistory(
          this.chatHistorySearchTerm,
          this.chatHistoryFilters.page,
          this.chatHistoryFilters.per_page
        )
        .subscribe({
          next: (response) => {
            this.previousChats = response.items;
            this.chatHistoryMeta = response._meta;
            this.chatHistoryIsLoading = false;
          },
          error: () => {
            this.chatHistoryIsLoading = false;
          },
        })
    );
  }

  chatHistorySearchTermChanged(event: Event) {
    this.chatHistorySearchTerm = (<HTMLInputElement>event.target).value;
    this.chatHistoryFilters.page = 1;
    this.searchChatHistoryDebounce();
  }

  searchChatHistoryDebounce() {
    if (this.chatHistorySearchTimeout) {
      clearTimeout(this.chatHistorySearchTimeout);
    }
    this.chatHistorySearchTimeout = setTimeout(() => {
      this.loadConversationHistory();
    }, 300);
  }

  ngOnDestroy(): void {
    if (this.chatHistorySearchTimeout) {
      clearTimeout(this.chatHistorySearchTimeout);
    }
    this.subscriptions.unsubscribe();
  }
}
